import React, { useEffect, useRef, useState } from "react";
import { api, Banner, Button, Field, Modal, Icon } from "./ui.tsx";
import type { User } from "../shared/contracts.ts";
import type {
  Appointment,
  AppointmentInput,
  CalendarAccess,
  CalendarOccurrence,
} from "../shared/calendar.ts";
import "./calendar.css";
const today = () =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(
    new Date(),
  );
const label = (d: string) =>
  new Date(d + "T12:00:00Z").toLocaleDateString("pt-BR", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
const addDay = (d: string, n: number) =>
  new Date(Date.parse(d + "T12:00:00Z") + n * 86400000)
    .toISOString()
    .slice(0, 10);
const initial = (date: string): AppointmentInput => ({
  title: "",
  description: "",
  location: "",
  date,
  allDay: false,
  startTime: "09:00",
  endTime: "10:00",
  repeat: "none",
  until: "",
});
type CalendarOption = {
  id: string;
  name: string;
  editable: boolean;
  creatable: boolean;
  readable: boolean;
};
const calendarColor = (id: string) => {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return `hsl(${Math.abs(h) % 360} 58% 43%)`;
};
export function Calendar({
  user,
  onDirty,
  notify,
}: {
  user: User;
  onDirty: (v: boolean) => void;
  notify: (s: string) => void;
}) {
  const [calendars, setCalendars] = useState<CalendarOption[]>([]),
    [owner, setOwner] = useState(user.id),
    [together, setTogether] = useState(false),
    [included, setIncluded] = useState<string[] | null>(null),
    [selected, setSelected] = useState(today),
    [view, setView] = useState("month"),
    [rows, setRows] = useState<CalendarOccurrence[]>([]),
    [loading, setLoading] = useState(true),
    [accessLoading, setAccessLoading] = useState(true),
    [error, setError] = useState(""),
    [refresh, setRefresh] = useState(0),
    [form, setForm] = useState<{
      input: AppointmentInput;
      ownerId: string;
      record?: Appointment;
      occurrence?: string;
      scope: "series" | "occurrence";
    } | null>(null),
    [busy, setBusy] = useState(false),
    [admin, setAdmin] = useState(false);
  const activities = useRef<HTMLHeadingElement>(null),
    op = useRef(crypto.randomUUID());
  useEffect(() => {
    op.current = crypto.randomUUID();
  }, [form]);
  const editable = calendars.find((c) => c.id === owner)?.editable;
  const creatable = calendars.find((c) => c.id === owner)?.creatable;
  const selectedOwners = calendars
    .filter((c) => c.readable && (!included || included.includes(c.id)))
    .map((c) => c.id);
  const month = selected.slice(0, 7),
    monthFrom = month + "-01",
    monthTo = addDay(
      new Date(
        Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)), 1, 12),
      )
        .toISOString()
        .slice(0, 10),
      -1,
    );
  const weekStart = addDay(
    selected,
    -((new Date(selected + "T12:00:00Z").getUTCDay() + 6) % 7),
  );
  const from = view === "week" ? weekStart : monthFrom;
  const to = view === "week" ? addDay(weekStart, 6) : monthTo;
  useEffect(() => {
    const abort = new AbortController();
    setAccessLoading(true);
    api<CalendarOption[]>("/calendar/access", "GET", undefined, abort.signal)
      .then((list) => {
        setCalendars(list);
        if (!list.some((c) => c.id === owner)) setOwner(list[0]?.id ?? "");
      })
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!abort.signal.aborted) setAccessLoading(false);
      });
    return () => abort.abort();
  }, [user, refresh]);
  useEffect(() => {
    if (
      (!together &&
        (!owner || !calendars.some((c) => c.id === owner && c.readable))) ||
      (together && !selectedOwners.length)
    ) {
      setRows([]);
      setLoading(false);
      return;
    }
    const abort = new AbortController();
    setLoading(true);
    setRows([]);
    setError("");
    api<CalendarOccurrence[]>(
      together
        ? `/calendar/combined?owners=${encodeURIComponent(selectedOwners.join(","))}&from=${from}&to=${to}`
        : `/calendar/appointments?ownerId=${encodeURIComponent(owner)}&from=${from}&to=${to}`,
      "GET",
      undefined,
      abort.signal,
    )
      .then(setRows)
      .catch((e) => {
        if (e.name !== "AbortError") {
          setRows([]);
          setError(e.message);
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [owner, from, to, refresh, calendars, together, included]);
  function choose(day: string) {
    setSelected(day);
    requestAnimationFrame(() => {
      activities.current?.focus({ preventScroll: true });
      activities.current?.scrollIntoView({
        block: "start",
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
    });
  }
  async function edit(row: CalendarOccurrence) {
    try {
      const record = await api<Appointment>(
        "/calendar/appointments/" + row.seriesId,
      );
      setForm({
        ownerId: row.ownerId,
        input: {
          title: row.title,
          description: row.description,
          location: row.location,
          date: row.date,
          allDay: row.allDay,
          startTime: row.startTime,
          endTime: row.endTime,
          repeat: "none",
          until: "",
        },
        record,
        occurrence: row.originalDate,
        scope: "occurrence",
      });
      op.current = crypto.randomUUID();
    } catch (e: any) {
      setError(e.message);
    }
  }
  function close() {
    setForm(null);
    onDirty(false);
  }
  async function save(cancel = false) {
    if (!form) return;
    const clashes = rows.filter(
      (r) =>
        r.ownerId === form.ownerId &&
        r.date === form.input.date &&
        !(
          r.seriesId === form.record?.id &&
          (form.scope === "series" || r.originalDate === form.occurrence)
        ) &&
        (r.allDay ||
          form.input.allDay ||
          (r.startTime < form.input.endTime &&
            r.endTime > form.input.startTime)),
    );
    if (
      !cancel &&
      clashes.length &&
      !window.confirm(
        `Há ${clashes.length} compromisso(s) nesse horário entre os registros consultados. Salvar mesmo assim?`,
      )
    )
      return;
    if (
      cancel &&
      !window.confirm(
        form.scope === "series"
          ? "Cancelar toda esta série de compromissos?"
          : "Cancelar este compromisso?",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api("/calendar/appointments", "POST", {
        operationId: op.current,
        ownerId: form.ownerId,
        id: form.record?.id,
        version: form.record?.version,
        scope: form.scope,
        originalDate: form.occurrence,
        value: form.input,
        cancel,
      });
      close();
      setRefresh((n) => n + 1);
      notify(
        cancel
          ? "Compromisso cancelado no servidor."
          : "Compromisso salvo no servidor.",
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const firstWeekDay = (new Date(monthFrom + "T12:00:00Z").getUTCDay() + 6) % 7;
  const allDays = Array.from(
    { length: Number(monthTo.slice(-2)) },
    (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`,
  );
  const days =
    view === "day"
      ? [selected]
      : view === "week"
        ? Array.from({ length: 7 }, (_, i) => addDay(weekStart, i))
        : allDays;
  const visible = rows.filter((r) => r.date === selected);
  return (
    <section className="calendar-module">
      <div className="section-title">
        <h1>Calendário</h1>
        {user.admin && (
          <details className="module-options">
            <summary>Opções</summary>
            <Button onClick={() => setAdmin(true)}>Acessos às agendas</Button>
          </details>
        )}
      </div>
      <p className="calendar-context">Horário de Brasília</p>
      {error && (
        <Banner kind="danger">
          {error}{" "}
          <Button onClick={() => setRefresh((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      {accessLoading ? (
        <p role="status">Carregando acesso às agendas…</p>
      ) : !calendars.length && !error ? (
        <Banner>
          Seu acesso ao calendário ainda não foi habilitado pelo administrador.
        </Banner>
      ) : calendars.length > 0 ? (
        <>
          <div className="calendar-quick-actions">
            <span>
              {together
                ? "Agendas selecionadas"
                : owner === user.id
                  ? "Minha agenda"
                  : calendars.find((c) => c.id === owner)?.name}
              {together || editable ? "" : " · somente leitura"}
            </span>
            {(creatable ||
              (together && calendars.some((c) => c.creatable))) && (
              <Button
                kind="primary"
                onClick={() => {
                  op.current = crypto.randomUUID();
                  setForm({
                    input: initial(selected),
                    ownerId:
                      calendars.find((c) => c.id === owner && c.creatable)
                        ?.id ?? calendars.find((c) => c.creatable)!.id,
                    scope: "series",
                  });
                }}
              >
                Novo compromisso
              </Button>
            )}
          </div>
          <details className="calendar-selector">
            <summary>Escolher agenda ou data</summary>
            {calendars.length > 1 && (
              <label className="calendar-reader">
                <input
                  type="checkbox"
                  checked={together}
                  onChange={(e) => setTogether(e.target.checked)}
                />
                Ver agendas juntas
              </label>
            )}
            <div className="calendar-toolbar">
              {!together && (
                <Field label="Agenda">
                  <select
                    value={owner}
                    onChange={(e) => setOwner(e.target.value)}
                  >
                    {calendars.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.id === user.id ? "Minha agenda" : c.name}
                        {c.editable ? "" : " · somente leitura"}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Data">
                <input
                  type="date"
                  value={selected}
                  onChange={(e) => {
                    if (e.target.value) setSelected(e.target.value);
                  }}
                />
              </Field>
            </div>
            {together && (
              <fieldset className="calendar-legend">
                <legend>Agendas exibidas</legend>
                {calendars
                  .filter((c) => c.readable)
                  .map((c) => (
                    <label key={c.id}>
                      <input
                        type="checkbox"
                        checked={selectedOwners.includes(c.id)}
                        onChange={(e) =>
                          setIncluded(
                            e.target.checked
                              ? [...selectedOwners, c.id]
                              : selectedOwners.filter((id) => id !== c.id),
                          )
                        }
                      />
                      <span
                        className="calendar-color"
                        style={{ background: calendarColor(c.id) }}
                        aria-hidden="true"
                      />
                      {c.name}
                    </label>
                  ))}
              </fieldset>
            )}
          </details>
          <div className="calendar-workspace">
            <section
              className="calendar-surface"
              aria-label="Calendário e seleção de dia"
            >
              <div className="calendar-month-heading">
                <Button
                  aria-label="Mês anterior"
                  onClick={() =>
                    setSelected(
                      new Date(
                        Date.UTC(
                          Number(month.slice(0, 4)),
                          Number(month.slice(5)) - 2,
                          1,
                          12,
                        ),
                      )
                        .toISOString()
                        .slice(0, 10),
                    )
                  }
                >
                  <Icon name="back" />
                </Button>
                <h2>
                  {new Date(monthFrom + "T12:00:00Z").toLocaleDateString(
                    "pt-BR",
                    { month: "long", year: "numeric", timeZone: "UTC" },
                  )}
                </h2>
                <Button
                  aria-label="Próximo mês"
                  onClick={() =>
                    setSelected(
                      new Date(
                        Date.UTC(
                          Number(month.slice(0, 4)),
                          Number(month.slice(5)),
                          1,
                          12,
                        ),
                      )
                        .toISOString()
                        .slice(0, 10),
                    )
                  }
                >
                  <Icon name="arrow" />
                </Button>
              </div>
              <div className="calendar-modes" aria-label="Visualização">
                {[
                  ["month", "Mês"],
                  ["week", "Semana"],
                  ["day", "Dia"],
                ].map(([key, text]) => (
                  <Button
                    key={key}
                    aria-pressed={view === key}
                    onClick={() => setView(key)}
                  >
                    {text}
                  </Button>
                ))}
              </div>
              {view !== "day" && (
                <div className="calendar-weekdays" aria-hidden="true">
                  {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map(
                    (day) => (
                      <span key={day}>{day}</span>
                    ),
                  )}
                </div>
              )}
              <div
                className={"calendar-days " + view}
                aria-label="Selecionar dia"
              >
                {view === "month" &&
                  Array.from({ length: firstWeekDay }, (_, i) => (
                    <span key={"blank" + i} />
                  ))}
                {days.map((day) => (
                  <button
                    key={day}
                    className={day === selected ? "selected" : ""}
                    aria-label={
                      label(day) +
                      (rows.some((r) => r.date === day)
                        ? ", com compromissos"
                        : "")
                    }
                    aria-pressed={day === selected}
                    onClick={() => choose(day)}
                  >
                    {Number(day.slice(-2))}
                    {together && (
                      <span className="calendar-dots" aria-hidden="true">
                        {calendars
                          .filter(
                            (c) =>
                              selectedOwners.includes(c.id) &&
                              rows.some(
                                (r) => r.date === day && r.ownerId === c.id,
                              ),
                          )
                          .map((c) => (
                            <span
                              key={c.id}
                              className="calendar-color"
                              style={{ background: calendarColor(c.id) }}
                            />
                          ))}
                      </span>
                    )}
                    {!together && rows.some((r) => r.date === day) && (
                      <span aria-hidden="true"> •</span>
                    )}
                  </button>
                ))}
              </div>
            </section>
            <section
              className="calendar-agenda"
              aria-labelledby="calendar-selected-day"
            >
              <div className="calendar-agenda-heading">
                <span className="calendar-eyebrow">Agenda do dia</span>
                <h2 id="calendar-selected-day" ref={activities} tabIndex={-1}>
                  {label(selected)}
                </h2>
                {!loading && (
                  <span className="calendar-day-count">
                    {visible.length === 1
                      ? "1 compromisso"
                      : `${visible.length} compromissos`}
                  </span>
                )}
              </div>
              {loading ? (
                <p role="status">Carregando agenda…</p>
              ) : visible.length ? (
                visible.map((r) => (
                  <article
                    className="calendar-item"
                    style={
                      {
                        "--calendar-person-color": calendarColor(r.ownerId),
                      } as React.CSSProperties
                    }
                    key={r.seriesId + r.originalDate}
                  >
                    <div
                      className="calendar-time"
                      aria-label={
                        r.allDay
                          ? "Dia inteiro"
                          : `Das ${r.startTime} às ${r.endTime}`
                      }
                    >
                      <Icon name="clock" />
                      <strong>{r.allDay ? "Dia inteiro" : r.startTime}</strong>
                      {!r.allDay && <span>até {r.endTime}</span>}
                    </div>
                    <div className="calendar-item-content">
                      <h3>{r.title}</h3>
                      <div className="calendar-owner">
                        <span
                          className="calendar-person-avatar"
                          aria-hidden="true"
                        >
                          {(
                            (
                              calendars.find((c) => c.id === r.ownerId)?.name ??
                              ""
                            ).match(/\p{L}+/gu) ?? []
                          )
                            .slice(0, 2)
                            .map((n) => n[0])
                            .join("")}
                        </span>
                        {calendars.find((c) => c.id === r.ownerId)?.name}
                      </div>
                      {r.location && (
                        <p className="calendar-location">
                          <span>Local</span> {r.location}
                        </p>
                      )}
                      {r.description && (
                        <details className="calendar-description">
                          <summary>Ver detalhes</summary>
                          <p>{r.description}</p>
                        </details>
                      )}
                      {calendars.find((c) => c.id === r.ownerId)?.editable && (
                        <Button kind="ghost" onClick={() => edit(r)}>
                          Abrir e editar <Icon name="arrow" />
                        </Button>
                      )}
                    </div>
                  </article>
                ))
              ) : (
                <div className="calendar-empty">
                  <Icon name="calendar" />
                  <p>
                    {!together &&
                    !calendars.find((c) => c.id === owner)?.readable
                      ? "Você pode adicionar compromissos nesta agenda, mas não consultar os compromissos existentes."
                      : together && !selectedOwners.length
                        ? "Selecione uma agenda para consultar."
                        : "Nenhum compromisso neste dia."}
                  </p>
                </div>
              )}
            </section>
          </div>
        </>
      ) : null}
      <details>
        <summary>Disponibilidade desta etapa</summary>
        <p>
          Compromissos persistidos no servidor. Prazos de demandas, lembretes e
          push ainda dependem da integração desses módulos. A gravação offline
          não está disponível; falhas preservam o formulário enquanto esta
          página estiver aberta.
        </p>
      </details>
      {form && (
        <Modal
          title={form.record ? "Editar compromisso" : "Novo compromisso"}
          mobileFullScreen
          className="guided-form-dialog"
          onClose={() => {
            if (
              !busy &&
              window.confirm(
                "Fechar o formulário? Alterações não salvas serão descartadas.",
              )
            )
              close();
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <fieldset disabled={busy}>
              <div className="guided-form-body">
                {error && <Banner kind="danger">{error}</Banner>}
                {!form.record && (
                  <Field label="Agenda do compromisso">
                    <select
                      value={form.ownerId}
                      onChange={(e) => {
                        setForm({ ...form, ownerId: e.target.value });
                        onDirty(true);
                      }}
                    >
                      {calendars
                        .filter((c) => c.creatable)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </Field>
                )}
                {form.record && form.record.repeat !== "none" && (
                  <Field label="Alterar">
                    <select
                      value={form.scope}
                      onChange={(e) => {
                        const scope = e.target.value as "series" | "occurrence";
                        const r = form.record!;
                        setForm({
                          ...form,
                          scope,
                          input:
                            scope === "series"
                              ? {
                                  title: r.title,
                                  description: r.description,
                                  location: r.location,
                                  date: r.date,
                                  allDay: r.allDay,
                                  startTime: r.startTime,
                                  endTime: r.endTime,
                                  repeat: r.repeat,
                                  until: r.until,
                                }
                              : {
                                  ...form.input,
                                  repeat: "none",
                                  until: "",
                                  date: form.occurrence!,
                                },
                        });
                        onDirty(true);
                      }}
                    >
                      <option value="occurrence">
                        Somente esta ocorrência
                      </option>
                      <option value="series">
                        Toda a série (exceções individuais preservadas)
                      </option>
                    </select>
                  </Field>
                )}
                {(
                  [
                    ["title", "Título"],
                    ["location", "Local"],
                    ["description", "Descrição"],
                  ] as const
                ).map(([key, text]) => (
                  <Field key={key} label={text}>
                    <input
                      required={key === "title"}
                      value={form.input[key]}
                      onChange={(e) => {
                        setForm({
                          ...form,
                          input: { ...form.input, [key]: e.target.value },
                        });
                        onDirty(true);
                      }}
                    />
                  </Field>
                ))}
                <Field label="Data">
                  <input
                    required
                    type="date"
                    value={form.input.date}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        input: { ...form.input, date: e.target.value },
                      });
                      onDirty(true);
                    }}
                  />
                </Field>
                <label>
                  <input
                    type="checkbox"
                    checked={form.input.allDay}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        input: { ...form.input, allDay: e.target.checked },
                      });
                      onDirty(true);
                    }}
                  />{" "}
                  Dia inteiro
                </label>
                {!form.input.allDay &&
                  (
                    [
                      ["startTime", "Início"],
                      ["endTime", "Fim"],
                    ] as const
                  ).map(([key, text]) => (
                    <Field key={key} label={text}>
                      <input
                        required
                        type="time"
                        value={form.input[key]}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            input: { ...form.input, [key]: e.target.value },
                          });
                          onDirty(true);
                        }}
                      />
                    </Field>
                  ))}
                {form.scope === "series" && (
                  <Field label="Repetição">
                    <select
                      value={form.input.repeat}
                      onChange={(e) => {
                        setForm({
                          ...form,
                          input: {
                            ...form.input,
                            repeat: e.target
                              .value as AppointmentInput["repeat"],
                          },
                        });
                        onDirty(true);
                      }}
                    >
                      <option value="none">Não repetir</option>
                      <option value="daily">Todos os dias</option>
                      <option value="weekly">Toda semana</option>
                      <option value="monthly">
                        Todo mês, no mesmo dia (quando existir)
                      </option>
                    </select>
                  </Field>
                )}
                {form.input.repeat !== "none" && (
                  <Field label="Repetir até (opcional)">
                    <input
                      type="date"
                      min={form.input.date}
                      value={form.input.until}
                      onChange={(e) => {
                        setForm({
                          ...form,
                          input: { ...form.input, until: e.target.value },
                        });
                        onDirty(true);
                      }}
                    />
                  </Field>
                )}
              </div>
              <div className="guided-form-actions actions">
                <Button kind="primary" type="submit">
                  {busy ? "Salvando…" : "Salvar compromisso"}
                </Button>
                {form.record && (
                  <Button type="button" onClick={() => save(true)}>
                    Cancelar compromisso
                  </Button>
                )}
              </div>
            </fieldset>
          </form>
        </Modal>
      )}
      {admin && (
        <CalendarAdmin
          onClose={() => {
            setAdmin(false);
            setRefresh((n) => n + 1);
          }}
        />
      )}
    </section>
  );
}
function CalendarAdmin({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<any>(),
    [grants, setGrants] = useState<Record<string, CalendarAccess>>({}),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    const d = await api("/calendar/settings");
    setData(d);
    setGrants(d.draft?.grants ?? d.published.grants);
  }
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  async function save(publish: boolean) {
    setBusy(true);
    setError("");
    try {
      await api(
        "/calendar/settings/" + (publish ? "publish" : "draft"),
        publish ? "POST" : "PUT",
        {
          revision: Math.max(
            data.draft?.revision ?? 0,
            data.published.revision,
          ),
          grants,
        },
      );
      await load();
      setMessage(
        publish
          ? "Acessos publicados."
          : "Rascunho salvo, sem alterar acessos.",
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Acessos às agendas" mobileFullScreen onClose={onClose}>
      {error && <Banner kind="danger">{error}</Banner>}
      {message && <Banner>{message}</Banner>}
      <p>
        O titular pode editar sua agenda. Consulta e criação são autorizações
        separadas. Criar não permite alterar compromissos existentes.
        Administrador pode editar todas. Não há convites nem compartilhamento de
        compromissos.
      </p>
      {data ? (
        <>
          <fieldset disabled={busy}>
            {data.users.map((u: any) => {
              const g = grants[u.id] ?? { enabled: false, readers: [] };
              return (
                <details key={u.id}>
                  <summary>
                    {u.name}
                    {!u.active ? " · desativado" : ""}
                  </summary>
                  <label>
                    <input
                      type="checkbox"
                      checked={g.enabled}
                      onChange={(e) =>
                        setGrants({
                          ...grants,
                          [u.id]: { ...g, enabled: e.target.checked },
                        })
                      }
                    />{" "}
                    Habilitar calendário
                  </label>
                  <p>Quem poderá consultar esta agenda:</p>
                  {data.users
                    .filter((r: any) => r.id !== u.id && r.active)
                    .map((r: any) => (
                      <label className="calendar-reader" key={r.id}>
                        <input
                          type="checkbox"
                          checked={g.readers.includes(r.id)}
                          onChange={(e) =>
                            setGrants({
                              ...grants,
                              [u.id]: {
                                ...g,
                                readers: e.target.checked
                                  ? [...g.readers, r.id]
                                  : g.readers.filter((id) => id !== r.id),
                              },
                            })
                          }
                        />
                        {r.name}
                      </label>
                    ))}
                  <p>Quem poderá adicionar compromissos nesta agenda:</p>
                  {data.users
                    .filter((r: any) => r.id !== u.id && r.active)
                    .map((r: any) => (
                      <label className="calendar-reader" key={r.id}>
                        <input
                          type="checkbox"
                          checked={(g.creators ?? []).includes(r.id)}
                          onChange={(e) =>
                            setGrants({
                              ...grants,
                              [u.id]: {
                                ...g,
                                creators: e.target.checked
                                  ? [...(g.creators ?? []), r.id]
                                  : (g.creators ?? []).filter(
                                      (id) => id !== r.id,
                                    ),
                              },
                            })
                          }
                        />
                        {r.name}
                      </label>
                    ))}
                </details>
              );
            })}
            <p>
              Prévia: {Object.values(grants).filter((g) => g.enabled).length}{" "}
              calendários habilitados. Publicar substitui os acessos
              nominativos; não altera compromissos.
            </p>
            <Button onClick={() => save(false)}>Salvar rascunho</Button>
            <Button kind="primary" onClick={() => save(true)}>
              Publicar acessos
            </Button>
            <details>
              <summary>Restaurar configuração anterior para revisão</summary>
              {data.versions.map((v: any) => (
                <p key={v.revision}>
                  Versão {v.revision}{" "}
                  <Button
                    onClick={() => {
                      setGrants(v.grants);
                      setMessage(
                        "Revise os acessos e publique para restaurar.",
                      );
                    }}
                  >
                    Carregar
                  </Button>
                </p>
              ))}
            </details>
          </fieldset>
        </>
      ) : (
        <p>Carregando acessos…</p>
      )}
    </Modal>
  );
}
